'use client';

import { Elements } from '@stripe/react-stripe-js';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import { ReactNode, useEffect, useState } from 'react';

import { useRuntimeEnv } from '@/components/runtime-env';

let stripePromise: Promise<Stripe | null> | null = null;

const getStripe = (key: string | undefined) => {
    if (!stripePromise) {
        if (!key) {
            console.error('Missing STRIPE_PUBLISHABLE_KEY');
            return null;
        }
        stripePromise = loadStripe(key);
    }
    return stripePromise;
};

export interface BillingDetails {
    email?: string;
    name?: string;
    companyName?: string;
    vatNumber?: string;
    phone?: string;
    address?: {
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        postal_code?: string;
        country?: string;
    };
}

interface StripeProviderProps {
    children: ReactNode;
    clientSecret?: string;
}

export function StripeProvider({ children, clientSecret }: StripeProviderProps) {
    const runTimeEnv = useRuntimeEnv();

    const [stripe, setStripe] = useState<Promise<Stripe | null> | null>(null);

    const envPending = runTimeEnv.pending;
    const stripeKey = runTimeEnv.STRIPE_PUBLISHABLE_KEY;

    useEffect(() => {
        if (envPending) {
            // Wait until env is loaded
            return;
        }

        setStripe(getStripe(stripeKey));
    }, [envPending, stripeKey]);

    if (!stripe) {
        return null; // Don't render children until stripe is loaded
    }

    const appearance = {
        theme: 'night' as const,
        variables: {
            colorPrimary: '#10b981',
            colorBackground: '#0a0a0a',
            colorText: '#ffffff',
            colorDanger: '#ef4444',
            fontFamily: 'system-ui, sans-serif',
            borderRadius: '8px',
        },
    };

    const options = clientSecret
        ? {
              clientSecret,
              appearance,
          }
        : {
              appearance,
          };

    return (
        <Elements stripe={stripe} options={options}>
            {children}
        </Elements>
    );
}
